import { Chip } from '@hologram-dimension/chip';
import { IconNames } from '@hologram-dimension/icons';
import useSimDetails from 'api/sims/useSimDetails';
import useSimProfiles from 'api/sims/useSimProfiles';
import Field from 'components/Field/Field';
import { FC, Fragment, useMemo } from 'react';
import useStatusMap from 'utils/hooks/useStatusMap';
import useAlgoliaFilter from 'components/CoverageSearch/useAlgoliaFilter';
import useAlgoliaMeta from 'components/CoverageSearch/useAlgoliaMeta';
import clsx from 'clsx';
import useUserInfo from 'hooks/User/useUserInfo';
import useOrganization from 'state/contexts/Organization/useOrganization';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import useSimStatuses from 'api/sims/useSimStatuses';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import formatMoney from 'utils/numbers/formatMoney';
import useBillingRates from 'api/billing/useBillingRates';
import { Icon } from '@hologram-dimension/icon';
import useSimBilling from 'api/sims/useSimBilling';
import { calculateRateRange } from 'components/CoverageSearch/searchUtils';
import styles from './SimCoveragePanelHeader.module.css';

interface SimCoveragePanelHeaderProps {
  hologramSimId?: HologramSimId;
}

const Profiles: FC<{ hologramSimId?: HologramSimId }> = ({ hologramSimId }) => {
  const {
    simDetails,
    isLoading: isSimDetailsLoading,
    error: simDetailsError,
  } = useSimDetails(hologramSimId);
  const {
    simProfiles,
    isLoading: isSimProfilesLoading,
    error: simProfilesError,
  } = useSimProfiles(hologramSimId);

  // TODO: Remove when we have real connected status
  const {
    deviceDetails,
    isLoading: isDeviceDetailsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);
  const { isSuperAdmin } = useUserInfo();
  const organizationId = useOrganization();

  const { deviceIdsWithOpenSessions } = useOpenSessions(
    organizationId,
    isSuperAdmin || !!organizationId
  );
  const { networkStatusGroups } = useSimStatuses();

  const realNetworkState = useMemo(() => {
    if (
      deviceDetails.claimedDeviceId &&
      deviceIdsWithOpenSessions.has(deviceDetails.claimedDeviceId) &&
      !networkStatusGroups?.deactivated?.includes(simDetails.networkStatus as any)
    ) {
      if (networkStatusGroups?.predeployment?.includes(simDetails.networkStatus as any)) {
        return 'TEST-CONNECTED';
      }
      return 'CONNECTED';
    }
    return simDetails.networkStatus;
  }, [
    deviceDetails.claimedDeviceId,
    deviceIdsWithOpenSessions,
    simDetails.networkStatus,
    networkStatusGroups?.predeployment,
    networkStatusGroups?.deactivated,
  ]);
  const {
    statusInfo,
    isLoading: isStatusInfoLoading,
    error: statusInfoError,
  } = useStatusMap(realNetworkState);

  const isLoading =
    isSimDetailsLoading || isSimProfilesLoading || isStatusInfoLoading || isDeviceDetailsLoading;
  const error = simDetailsError || simProfilesError || statusInfoError || deviceDetailsError;
  if (isLoading)
    return <span className={clsx(styles.diminished, styles.profilesLoading)}>Loading&hellip;</span>;
  if (error) return '—';
  return (
    <div className={styles.profiles}>
      {simProfiles.map((profile, index) => {
        const isActiveProfile = profile.installationState === 'ENABLED';
        let icon: IconNames | undefined;
        if (isActiveProfile) icon = statusInfo.icon;
        else if (simDetails.fallbackEnabled && simDetails.fallbackInstalled) icon = 'Shield';
        return (
          <Fragment key={profile.iccid}>
            <Chip
              size="medium"
              colorVariant={
                profile.installationState === 'ENABLED' ? statusInfo?.colorVariant : undefined
              }
              iconStart={icon}
              key={profile.simProfileId}
            >
              {profile.profileName}
            </Chip>
            {index + 1 < simProfiles.length && '+'}
          </Fragment>
        );
      })}
    </div>
  );
};

const SimCoveragePanelHeader: FC<SimCoveragePanelHeaderProps> = ({ hologramSimId }) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: isSimDetailsLoading,
  } = useSimDetails(hologramSimId);
  const { simProfiles, error: simProfilesError } = useSimProfiles(hologramSimId);
  const searchFilter = useAlgoliaFilter({ simProfiles });
  const { data: meta, error: metaError, isLoading: isMetaLoading } = useAlgoliaMeta(searchFilter);
  const {
    billingRates,
    error: billingRatesError,
    isLoading: isBillingRatesLoading,
  } = useBillingRates(simDetails.organizationId);
  const {
    simBilling,
    error: simBillingError,
    isLoading: isSimBillingLoading,
  } = useSimBilling(hologramSimId);

  const rateRange = useMemo(
    () => calculateRateRange(billingRates, meta, simBilling.dataRate ?? simBilling.overageCharge),
    [billingRates, meta, simBilling]
  );

  const rateRangeLabel = useMemo(() => {
    if (!rateRange.isFlatRate) {
      return 'Per MB rates';
    }
    if (simBilling.overageCharge) {
      return 'Overage per MB';
    }
    return 'Per MB rate';
  }, [rateRange.isFlatRate, simBilling.overageCharge]);

  const rateRangeContent = useMemo(() => {
    if (isBillingRatesLoading || isMetaLoading || isSimDetailsLoading || isSimBillingLoading) {
      return <span className={styles.diminished}>Loading&hellip;</span>;
    }
    if (billingRatesError || metaError || simBillingError)
      return (
        <span className={styles.diminished}>
          <Icon name="Warning" size="small" fill="DdsColorFillNotificationError" /> Unable to load
        </span>
      );
    if (rateRange.isFlatRate) {
      return `${formatMoney(rateRange.rateMin)}`;
    }
    return `${formatMoney(rateRange.rateMin)} – ${formatMoney(rateRange.rateMax)}`;
  }, [
    isBillingRatesLoading,
    isMetaLoading,
    isSimDetailsLoading,
    isSimBillingLoading,
    billingRatesError,
    metaError,
    simBillingError,
    rateRange.isFlatRate,
    rateRange.rateMin,
    rateRange.rateMax,
  ]);

  const error =
    metaError || simProfilesError || billingRatesError || simDetailsError || simBillingError;

  if (error) return null; // For now, since this is non-critical information

  return (
    <div className={styles.fields}>
      <Field
        direction="column"
        label="Networks"
        value={
          isMetaLoading ? (
            <span className={styles.diminished}>Loading&hellip;</span>
          ) : (
            meta?.networks
          )
        }
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label="Countries"
        value={
          isMetaLoading ? (
            <span className={styles.diminished}>Loading&hellip;</span>
          ) : (
            meta?.countries
          )
        }
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label={rateRangeLabel}
        value={rateRangeContent}
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label="Available profiles"
        value={<Profiles hologramSimId={hologramSimId} />}
      />
    </div>
  );
};

export default SimCoveragePanelHeader;
